import React from "react";
import { ChevronLeft } from "lucide-react";
import { C } from "../../constants/design";
import { useI18n } from "../../i18n/I18nContext";
import { Power } from "../../types/game";
import { EFFECT_ICONS } from "../../constants/league";
import { powerDesc } from "../../constants/crafting";
import {
  computeMatchStats,
  MatchTeamStats,
  MatchStatsSheet,
  MatchAction,
  MatchSimResult,
  DebugEntry,
  TeamBundles,
  playerBundles,
  duelProbability,
  SAVE_MIN,
  SAVE_MAX,
} from "../../engine/matchEngine";
import { LeagueMember } from "../../types/game";

/* ─── Feuille de match ──────────────────────────────────────────────────────
   Page dédiée d'un match terminé. Aujourd'hui : en-tête (équipes + score),
   comparatif d'équipe dérivé de la formule soft-cap (cf. computeMatchStats),
   et sort utilisé. Vouée à s'enrichir (grosses actions nommées, etc.). */

type TFn = (path: string, ...args: any[]) => string;

function getStatRows(t: TFn): Array<{ key: keyof MatchTeamStats; label: string; pct?: boolean }> {
  return [
    { key: "possession",    label: t("matchSheet.statPossession"),   pct: true },
    { key: "shots",         label: t("matchSheet.statShots") },
    { key: "shotsOnTarget", label: t("matchSheet.statShotsOnTarget") },
    { key: "saves",         label: t("matchSheet.statSaves") },
    { key: "passes",        label: t("matchSheet.statPasses") },
    { key: "passAccuracy",  label: t("matchSheet.statPassAccuracy"), pct: true },
    { key: "interceptions", label: t("matchSheet.statInterceptions") },
    { key: "dribbles",      label: t("matchSheet.statDribbles") },
    { key: "tackles",       label: t("matchSheet.statTackles") },
    { key: "counters",      label: t("matchSheet.statCounters") },
  ];
}

function StatComparison({
  home,
  away,
  t,
}: {
  home: MatchTeamStats;
  away: MatchTeamStats;
  t: TFn;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {getStatRows(t).map(({ key, label, pct }) => {
        const h = home[key];
        const a = away[key];
        const total = h + a || 1;
        const hShare = Math.round((h / total) * 100);
        const fmt = (v: number) => (pct ? `${v}%` : String(v));
        const hLead = h >= a;
        return (
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: hLead ? 900 : 600, color: hLead ? C.text : C.muted }}>
                {fmt(h)}
              </span>
              <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
              <span style={{ fontWeight: !hLead ? 900 : 600, color: !hLead ? C.text : C.muted }}>
                {fmt(a)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                height: 5,
                borderRadius: 3,
                overflow: "hidden",
                background: "rgba(255,255,255,0.06)",
              }}
            >
              <div style={{ width: `${hShare}%`, background: C.turf }} />
              <div style={{ width: `${100 - hShare}%`, background: "#ff6b6b" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const f1 = (n: number) => n.toFixed(1);

function getOutcomeLabel(t: TFn): Record<string, { icon: string; color: string; label: string }> {
  return {
    goal:         { icon: "⚽", color: "#2de36b", label: t("matchSheet.outcomeGoal") },
    shot_saved:   { icon: "🧤", color: "#5BC8D6", label: t("matchSheet.outcomeShotSaved") },
    shot_off:     { icon: "💨", color: C.muted,   label: t("matchSheet.outcomeShotOff") },
    tackle:       { icon: "🛡️", color: "#a78bfa", label: t("matchSheet.outcomeTackle") },
    interception: { icon: "✋", color: "#a78bfa", label: t("matchSheet.outcomeInterception") },
    counter:      { icon: "🔄", color: C.gold,    label: t("matchSheet.outcomeCounter") },
    buildup:      { icon: "🔁", color: C.faint,   label: t("matchSheet.outcomeBuildup") },
  };
}

/** Comparatif Contrôle (possession) — collectif équipe. */
function ControlRow({ home, away, homeName, awayName, t }: {
  home: number; away: number; homeName: string; awayName: string; t: TFn;
}) {
  const total = home + away || 1;
  const hShare = Math.round((home / total) * 100);
  const hLead = home >= away;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span style={{ fontWeight: hLead ? 800 : 500, color: hLead ? "#2de36b" : C.muted }}>{f1(home)} — {homeName}</span>
        <span style={{ color: C.faint, fontSize: 11 }}>{t("matchSheet.controlLabel")}</span>
        <span style={{ fontWeight: !hLead ? 800 : 500, color: !hLead ? "#ff8b8b" : C.muted }}>{awayName} — {f1(away)}</span>
      </div>
      <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: "rgba(255,255,255,0.06)" }}>
        <div style={{ width: `${hShare}%`, background: "#2de36b" }} />
        <div style={{ width: `${100 - hShare}%`, background: "#ff6b6b" }} />
      </div>
    </div>
  );
}

/** Inspecter un duel 1v1 entre un joueur sélectionné de chaque côté. */
function DuelInspector({ homeMembers, awayMembers, homeBundles, awayBundles, homeName, awayName, t }: {
  homeMembers: LeagueMember[];
  awayMembers: LeagueMember[];
  homeBundles: TeamBundles;
  awayBundles: TeamBundles;
  homeName: string;
  awayName: string;
  t: TFn;
}) {
  const [homeIdx, setHomeIdx] = React.useState(0);
  const [awayIdx, setAwayIdx] = React.useState(0);

  const hPlayer = homeMembers[homeIdx];
  const aPlayer = awayMembers[awayIdx];
  if (!hPlayer || !aPlayer) return null;

  const hb = playerBundles(hPlayer);
  const ab = playerBundles(aPlayer);

  // Stats brutes des deux joueurs (pour le comparatif visuel)
  const { profileFromPower } = (() => {
    const pf = (m: LeagueMember) => m.stats ?? { technique: m.power/4, defense: m.power/4, physique: m.power/4, vitesse: m.power/4 };
    return { profileFromPower: pf };
  })();
  const hStats = profileFromPower(hPlayer);
  const aStats = profileFromPower(aPlayer);

  // Bundles effectifs : valeurs individuelles × multiplicateurs de sort
  const hDrib   = hb.drib   * homeBundles.dribMult;
  const hFinish = hb.finish * homeBundles.finishMult;
  const hTackle = hb.tackle * homeBundles.tackleMult;
  const aDrib   = ab.drib   * awayBundles.dribMult;
  const aFinish = ab.finish * awayBundles.finishMult;
  const aTackle = ab.tackle * awayBundles.tackleMult;

  const saveFormula = (keeper: number, finish: number) => {
    const p = Math.min(SAVE_MAX, Math.max(SAVE_MIN, duelProbability(keeper, finish)));
    return t("matchSheet.gkFormula", pct(p), f1(keeper), f1(finish), String(Math.round(SAVE_MIN*100)), String(Math.round(SAVE_MAX*100)));
  };

  const selectStyle: React.CSSProperties = {
    background: "rgba(0,0,0,0.3)", color: C.text,
    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
    padding: "6px 8px", fontSize: 12, fontWeight: 600, flex: 1,
  };
  const duelRow = (label: string, attVal: number, defVal: number, attColor: string, defColor: string) => {
    const p = duelProbability(attVal, defVal);
    return (
      <div key={label} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <span style={{ color: attColor, fontWeight: 700 }}>{f1(attVal)}</span>
          <span style={{ color: C.faint, fontSize: 11 }}>{label} → <strong style={{ color: C.text }}>{pct(p)}</strong></span>
          <span style={{ color: defColor, fontWeight: 700 }}>{f1(defVal)}</span>
        </div>
        <div style={{ display: "flex", height: 4, borderRadius: 2, overflow: "hidden", background: "rgba(255,255,255,0.05)" }}>
          <div style={{ width: `${Math.round(p * 100)}%`, background: attColor, opacity: 0.7 }} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: 12, padding: 12, marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>
        {t("matchSheet.inspectTitle")}
      </div>
      {/* Sélecteurs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <select value={homeIdx} onChange={e => setHomeIdx(Number(e.target.value))} style={selectStyle}>
          {homeMembers.map((m, i) => <option key={i} value={i}>{m.name} (⚡{m.power})</option>)}
        </select>
        <span style={{ color: C.faint, alignSelf: "center", fontSize: 13 }}>vs</span>
        <select value={awayIdx} onChange={e => setAwayIdx(Number(e.target.value))} style={selectStyle}>
          {awayMembers.map((m, i) => <option key={i} value={i}>{m.name} (⚡{m.power})</option>)}
        </select>
      </div>

      {/* Stats brutes des deux joueurs */}
      {(() => {
        const STAT_ROWS_RAW = [
          { label: t("matchSheet.statTechnique"), hv: hStats.technique, av: aStats.technique },
          { label: t("matchSheet.statDefense"),   hv: hStats.defense,   av: aStats.defense   },
          { label: t("matchSheet.statPhysique"),  hv: hStats.physique,   av: aStats.physique  },
          { label: t("matchSheet.statVitesse"),   hv: hStats.vitesse,    av: aStats.vitesse   },
        ];
        return (
          <div style={{ marginBottom: 10, background: "rgba(0,0,0,0.2)", borderRadius: 8, padding: "8px 10px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>
              {t("matchSheet.rawStats")}
            </div>
            {STAT_ROWS_RAW.map(({ label, hv, av }) => {
              const hLead = hv >= av;
              const total = hv + av || 1;
              const hShare = Math.round((hv / total) * 100);
              return (
                <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}>
                    <span style={{ fontWeight: hLead ? 800 : 500, color: hLead ? "#2de36b" : C.muted, minWidth: 36 }}>{f1(hv)}</span>
                    <span style={{ color: C.faint, fontSize: 11 }}>{label}</span>
                    <span style={{ fontWeight: !hLead ? 800 : 500, color: !hLead ? "#ff8b8b" : C.muted, minWidth: 36, textAlign: "right" }}>{f1(av)}</span>
                  </div>
                  <div style={{ display: "flex", height: 3, borderRadius: 2, overflow: "hidden", background: "rgba(255,255,255,0.05)" }}>
                    <div style={{ width: `${hShare}%`, background: "#2de36b", opacity: 0.6 }} />
                    <div style={{ width: `${100 - hShare}%`, background: "#ff6b6b", opacity: 0.6 }} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Duels calculés */}
      <div>
        {duelRow(t("matchSheet.dribVsTackle"),        hDrib,   aTackle,  "#2de36b", "#ff8b8b")}
        {duelRow(t("matchSheet.shotVsBlock"),          hFinish, ab.block, "#2de36b", "#ff8b8b")}
        {duelRow(t("matchSheet.dribVsTackleCounter"),  aDrib,   hTackle,  "#ff8b8b", "#2de36b")}
        {duelRow(t("matchSheet.shotVsBlockCounter"),   aFinish, hb.block, "#ff8b8b", "#2de36b")}
        <div style={{ fontSize: 11, color: C.muted, padding: "6px 0" }}>
          🧤 Home GK vs away shot: <strong style={{ color: C.text }}>{saveFormula(homeBundles.keeper, aFinish)}</strong>
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>
          🧤 Away GK vs home shot: <strong style={{ color: C.text }}>{saveFormula(awayBundles.keeper, hFinish)}</strong>
        </div>
        {(homeBundles.dribMult !== 1 || homeBundles.finishMult !== 1 || homeBundles.tackleMult !== 1) && (
          <div style={{ fontSize: 11, color: "#C084FC", marginTop: 6 }}>
            {t("matchSheet.homeSpell", f1(homeBundles.dribMult), f1(homeBundles.finishMult), f1(homeBundles.tackleMult))}
          </div>
        )}
        {(awayBundles.dribMult !== 1 || awayBundles.finishMult !== 1 || awayBundles.tackleMult !== 1) && (
          <div style={{ fontSize: 11, color: "#fb923c", marginTop: 4 }}>
            {t("matchSheet.awaySpell", f1(awayBundles.dribMult), f1(awayBundles.finishMult), f1(awayBundles.tackleMult))}
          </div>
        )}
      </div>
    </div>
  );
}

function DebugLogEntry({ e, t }: { e: DebugEntry; t: TFn }) {
  const OUTCOME_LABEL = getOutcomeLabel(t);
  const oc = OUTCOME_LABEL[e.outcome] ?? { icon: "?", color: C.muted, label: e.outcome };
  const isGoal = e.outcome === "goal";

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 2,
      padding: "5px 8px", borderRadius: 7,
      background: isGoal ? (e.isHome ? "rgba(45,227,107,0.08)" : "rgba(255,80,80,0.08)") : "rgba(255,255,255,0.02)",
      borderLeft: `3px solid ${oc.color}`,
      fontSize: 10.5,
    }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ color: C.faint, minWidth: 26 }}>{e.minute}'</span>
        <span style={{ color: e.isHome ? "#2de36b" : "#ff8b8b", fontWeight: 700 }}>
          {e.attackerName ?? "?"}{e.isCounter ? " ↩" : ""}
        </span>
        <span style={{ color: C.faint }}>vs</span>
        <span style={{ color: e.isHome ? "#ff8b8b" : "#2de36b" }}>{e.defenderName ?? "?"}</span>
        <span style={{ marginLeft: "auto", color: oc.color, fontWeight: 800, whiteSpace: "nowrap" }}>{oc.icon} {oc.label}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", color: C.muted, paddingLeft: 32 }}>
        <span>
          🎯 <strong style={{ color: e.dribSuccess ? "#a3e635" : "#ff8b8b" }}>{pct(e.dribP)}</strong>
          {" "}(Dribble {f1(e.attDrib)} vs Tacle {f1(e.defTackle)}) → {e.dribSuccess ? "✅" : "❌"}
        </span>
        {e.shotTaken && e.onTargetP !== undefined && (
          <span>⚡ <strong style={{ color: e.onTarget ? "#a3e635" : "#ff8b8b" }}>{pct(e.onTargetP)}</strong>
            {" "}(Frappe {f1(e.attFinish ?? 0)} vs Bloc {f1(e.defBlock ?? 0)}) → {e.onTarget ? "✅" : "❌"}</span>
        )}
        {e.onTarget && e.saveP !== undefined && (
          <span>🧤 <strong style={{ color: e.saved ? "#ff8b8b" : "#2de36b" }}>{pct(e.saveP)}</strong>
            {" "}(Shot {f1(e.attFinish ?? 0)} vs GK) → {e.saved ? t("matchSheet.savedText") : t("matchSheet.goalText")}</span>
        )}
      </div>
    </div>
  );
}

/** Sélection des grosses actions à afficher (buts toujours inclus, puis tirs /
 *  tacles / cartons par ordre chronologique, plafonné pour rester lisible). */
function pickHighlights(events: MatchAction[], max = 14): MatchAction[] {
  const goals = events.filter((e) => e.type === "goal" || e.type === "opp_goal");
  const others = events
    .filter((e) => e.type === "shot" || e.type === "tackle" || e.type === "card")
    .slice(0, Math.max(0, max - goals.length));
  return [...goals, ...others].sort((a, b) => a.minute - b.minute);
}

const HIGHLIGHT_STYLE: Record<string, { color: string; bg: string }> = {
  goal: { color: C.turf, bg: "rgba(45,227,107,0.10)" },
  opp_goal: { color: "#ff6b6b", bg: "rgba(255,80,80,0.08)" },
  shot: { color: C.text, bg: "rgba(255,255,255,0.03)" },
  tackle: { color: "#5BC8D6", bg: "rgba(91,200,214,0.06)" },
  card: { color: C.gold, bg: "rgba(245,179,1,0.07)" },
};

interface MatchSheetPageProps {
  homeName: string;
  opponentName: string;
  scheduledAt: string;
  result: "win" | "draw" | "loss" | null;
  score: { gf: number; ga: number } | null;
  myTeamPower: number;
  oppPower: number;
  oppSeed: number;
  goalkeeperLevel: number;
  assignedPower?: Power | null;
  realStats?: MatchStatsSheet; // feuille figée (sinon dérivée des puissances)
  events?: MatchAction[]; // timeline figée (pour les faits marquants)
  testMode?: boolean; // bac à sable : simulation live relançable
  powers?: Power[]; // pouvoirs sélectionnables en test
  onRunTest?: (powerId?: string) => MatchSimResult;
  onBack: () => void;
}

export function MatchSheetPage({
  homeName,
  opponentName,
  scheduledAt,
  result,
  score,
  myTeamPower,
  oppPower,
  oppSeed,
  goalkeeperLevel,
  assignedPower,
  realStats,
  events,
  testMode,
  powers,
  onRunTest,
  onBack,
}: MatchSheetPageProps) {
  const { t, language } = useI18n();
  const [testSim, setTestSim] = React.useState<MatchSimResult | null>(null);
  const [testPowerId, setTestPowerId] = React.useState<string>("");

  // En test : tout (score, stats, timeline) vient de la dernière simulation.
  const effStats = testMode ? testSim?.stats : realStats;
  const effEvents = testMode ? testSim?.timeline : events;
  const effScore = testMode
    ? testSim
      ? { gf: testSim.homeScore, ga: testSim.awayScore }
      : null
    : score;
  const effResult = testMode ? testSim?.result ?? null : result;

  const sheet = React.useMemo(
    () =>
      effStats ??
      computeMatchStats(myTeamPower, oppPower, goalkeeperLevel, 1, oppSeed),
    [effStats, myTeamPower, oppPower, goalkeeperLevel, oppSeed]
  );
  const highlights = React.useMemo(
    () => (effEvents && effEvents.length ? pickHighlights(effEvents) : []),
    [effEvents]
  );

  const resCfg = {
    win:  { label: t("matchSheet.win"),  color: C.turf,   bg: "rgba(45,227,107,0.12)", border: "rgba(45,227,107,0.4)" },
    draw: { label: t("matchSheet.draw"), color: C.gold,   bg: "rgba(245,179,1,0.12)",  border: "rgba(245,179,1,0.4)" },
    loss: { label: t("matchSheet.loss"), color: "#ff6b6b", bg: "rgba(255,80,80,0.10)", border: "rgba(255,80,80,0.35)" },
  } as const;
  const rc = effResult ? resCfg[effResult] : null;

  const sectionTitle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 900,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    margin: "0 0 10px",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>
      {/* Header */}
      <button
        onClick={onBack}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          color: C.muted,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <ChevronLeft size={18} /> {t("matchSheet.back")}
      </button>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, marginBottom: 2 }}>
          {t("matchSheet.title")}
        </div>
        <div style={{ fontSize: 11.5, color: C.faint }}>
          {testMode
            ? t("matchSheet.sandboxMode")
            : `${new Date(scheduledAt).toLocaleDateString(language === "ko" ? "ko-KR" : "en-US", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })} · 16:00`}
        </div>
      </div>

      {/* Contrôles du bac à sable */}
      {testMode && onRunTest && (
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(168,85,247,0.3)",
            borderRadius: 16,
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <select
            value={testPowerId}
            onChange={(e) => setTestPowerId(e.target.value)}
            style={{
              background: C.surface2,
              color: C.text,
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              padding: "9px 10px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <option value="">{t("matchSheet.noSpell")}</option>
            {(powers ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setTestSim(onRunTest(testPowerId || undefined))}
            style={{
              background: "rgba(168,85,247,0.18)",
              border: "1px solid rgba(168,85,247,0.5)",
              borderRadius: 10,
              padding: "11px",
              fontSize: 14,
              fontWeight: 800,
              color: "#C084FC",
              cursor: "pointer",
            }}
          >
            {t("matchSheet.runTest")}
          </button>
        </div>
      )}

      {testMode && !testSim && (
        <div style={{ textAlign: "center", fontSize: 12.5, color: C.muted, padding: "10px 0" }}>
          {t("matchSheet.runTestHint")}
        </div>
      )}

      {/* Scoreline */}
      <div
        style={{
          background: C.surface,
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 16,
          padding: "18px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.turf, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {homeName}
          </div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>⚡ {myTeamPower}</div>
        </div>
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: 1 }}>
            {effScore ? `${effScore.gf} – ${effScore.ga}` : "—"}
          </div>
          {rc && (
            <div
              style={{
                marginTop: 4,
                display: "inline-block",
                background: rc.bg,
                border: `1px solid ${rc.border}`,
                borderRadius: 8,
                padding: "3px 10px",
                fontSize: 11,
                fontWeight: 900,
                color: rc.color,
              }}
            >
              {rc.label}
            </div>
          )}
        </div>
        <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#ff8b8b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {opponentName}
          </div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>
            ⚡ {testMode ? testSim?.oppPower ?? "?" : oppPower}
          </div>
        </div>
      </div>

      {/* Statistiques */}
      {(!testMode || testSim) && (
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <h3 style={sectionTitle}>{t("matchSheet.statsTitle")}</h3>
          <StatComparison home={sheet.home} away={sheet.away} t={t} />
        </div>
      )}

      {/* Faits marquants */}
      {highlights.length > 0 && (
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <h3 style={sectionTitle}>{t("matchSheet.highlightsTitle")}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {highlights.map((h, i) => {
              const st = HIGHLIGHT_STYLE[h.type] ?? HIGHLIGHT_STYLE.shot;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: st.bg,
                    borderRadius: 9,
                    padding: "8px 10px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      color: C.muted,
                      minWidth: 30,
                    }}
                  >
                    {h.minute}'
                  </span>
                  <span style={{ fontSize: 12.5, color: st.color, lineHeight: 1.3 }}>
                    {h.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Debug Engine — sandbox uniquement */}
      {testMode && testSim && testSim.debugLog.length > 0 && (
        <div style={{
          background: C.surface,
          border: "1px solid rgba(168,85,247,0.25)",
          borderRadius: 16,
          padding: 16,
        }}>
          <h3 style={sectionTitle}>{t("matchSheet.debugTitle")}</h3>

          {/* Contrôle d'équipe — collectif */}
          <ControlRow
            home={testSim.homeBundles.control}
            away={testSim.awayBundles.control}
            homeName={homeName}
            awayName={opponentName}
            t={t}
          />

          {/* Inspecteur 1v1 — individuel */}
          <DuelInspector
            homeMembers={testSim.homeMembers}
            awayMembers={testSim.awayMembers}
            homeBundles={testSim.homeBundles}
            awayBundles={testSim.awayBundles}
            homeName={homeName}
            awayName={opponentName}
            t={t}
          />

          {/* Log séquence par séquence */}
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", margin: "12px 0 6px" }}>
            {t("matchSheet.debugLog", testSim.debugLog.length)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 400, overflowY: "auto", paddingRight: 4 }}>
            {testSim.debugLog.map((e) => (
              <DebugLogEntry key={e.seq} e={e} t={t} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.faint, marginTop: 8, textAlign: "right" }}>
            {t("matchSheet.debugCurve")}
          </div>
        </div>
      )}

      {/* Sorts utilisés */}
      {!testMode && (
      <div
        style={{
          background: C.surface,
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 16,
          padding: 16,
        }}
      >
        <h3 style={sectionTitle}>{t("matchSheet.spellsTitle")}</h3>
        {assignedPower ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10,
              padding: "10px 12px",
            }}
          >
            <span style={{ fontSize: 20 }}>
              {EFFECT_ICONS[assignedPower.effectType] ?? "✨"}
            </span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800 }}>{assignedPower.name}</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>
                {powerDesc(assignedPower)}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            {t("matchSheet.noSpellUsed")}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
